import { css } from '@emotion/react'
import { useEditorState } from '@tiptap/react'
import classNames from 'classnames'
import { formatDistanceToNow } from 'date-fns'
import React, {
  useEffect,
  useMemo,
  useState,
  StrictMode,
  MouseEvent,
  KeyboardEvent,
  Fragment,
  useCallback,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { useResizeDetector } from 'react-resize-detector'
import {
  Button,
  Icon,
  DeprecatedSingleSelect,
  DeprecatedSingleSelectOption,
  Tooltip,
} from '@opallabs/lapidary'
import { TextEditorAsset, useTextEditor } from '../../../TextEditor'
import {
  ControlButton,
  ControlButtonGroup,
  ControlButtonPopover,
} from '../../../TextEditor/components/ControlBar'
import { useEditorVisibility } from '../../../TextEditor/hooks'
import {
  getActiveTextAlign,
  getAlignIcon,
  getHeadingIcon,
  getListIcon,
} from '../../../TextEditor/utils'
import { useFullControlBarStyles } from '../../hooks'
import { EditorSaveState } from '../../types'
import {
  AlignButtons,
  AttachmentButtons,
  ExportButtons,
  FormattingButtons,
  HeadingButtons,
  ListButtons,
  TableButtons,
} from './ButtonGroups'
import * as styles from './styles'

type FullControlBarProps = {
  alwaysVisible: boolean
  className: string
  disableDoneButton?: boolean
  disableFullScreenAndCenterControls?: boolean
  disableLastSavedTime?: boolean
  disableViewModeSelector?: boolean
  documentName?: string
  hasError: boolean
  isFullScreen: boolean
  onPickAssets?: () => Promise<TextEditorAsset[]>
  saveState?: EditorSaveState
  setFullScreen?: (state: boolean) => void
  setShowShortcuts: (state: boolean) => void
}

const viewModeOptions: DeprecatedSingleSelectOption[] = [
  {
    label: 'Viewing',
    value: 'viewing',
    leadingIcon: {
      glyph: 'gem-eye',
      color: 'primary',
      visibility: 'always',
    },
  },
  {
    label: 'Editing',
    value: 'editing',
    leadingIcon: {
      glyph: 'gem-edit',
      color: 'primary',
      visibility: 'always',
    },
  },
]

export const FullControlBar = React.forwardRef<
  HTMLDivElement,
  FullControlBarProps
>(
  (
    {
      alwaysVisible,
      className,
      documentName,
      hasError,
      isFullScreen,
      onPickAssets,
      saveState,
      setFullScreen,
      setShowShortcuts,
      disableDoneButton,
      disableFullScreenAndCenterControls,
      disableLastSavedTime,
      disableViewModeSelector,
    },
    outsideRef
  ) => {
    const { editor, theme, hyperlinkManager } = useTextEditor()
    const { ref: intersectionRef, inView } = useInView({
      rootMargin: '-95% 0px 0px 0px',
      root: document.body,
    })
    const { ref: resizeRef } = useResizeDetector({
      handleHeight: false,
      onResize: (width) => width !== undefined && setCurrentWidth(width),
      refreshMode: 'debounce',
      refreshRate: 100,
    })
    const {
      isActive,
      viewOnly,
      activeHeadingLevel,
      activeTextAlign,
      activeListType,
    } = useEditorState({
      editor,
      selector: (snapshot) => ({
        isActive: {
          table: snapshot.editor.can().deleteTable(),
        },
        viewOnly: !snapshot.editor.isEditable,
        activeHeadingLevel: [1, 2, 3, 4].find((level) =>
          snapshot.editor.isActive('heading', { level })
        ),
        activeTextAlign: getActiveTextAlign(snapshot.editor.state, 'left'),
        activeListType: ['bulletList', 'orderedList', 'taskList'].find((type) =>
          snapshot.editor.isActive(type)
        ),
      }),
    })
    const isVisible = useEditorVisibility({
      alwaysVisible,
      visibleOn: isFullScreen,
    })

    const [lastSaveTime, setLastSaveTime] = useState('')
    const [currentWidth, setCurrentWidth] = useState(0)

    const controlBarRefs = useCallback(
      (node?: HTMLDivElement | null) => {
        resizeRef(node)
        intersectionRef(node)
      },
      [intersectionRef, resizeRef]
    )
    const controlBarStyle = useMemo(
      () =>
        css(
          styles.controlBar({
            isFullScreen,
            isVisible,
            theme,
          })
        ),
      [isVisible, isFullScreen, theme]
    )
    const controlButtonStyleOverrides = useFullControlBarStyles()

    // Update formatted save time every 60 seconds
    useEffect(() => {
      const getFormattedTime = () => {
        setLastSaveTime(
          saveState?.lastUpdatedAt !== undefined
            ? `Saved ${formatDistanceToNow(saveState.lastUpdatedAt)} ago`
            : ''
        )
      }

      // Make initial call to set time now
      getFormattedTime()
      // Update every 60 seconds afterwards
      const timerId = setInterval(getFormattedTime, 1000 * 60)
      return () => {
        clearInterval(timerId)
      }
    }, [saveState?.lastUpdatedAt])

    // Handle done button action on mousedown or on keydown.
    // Safari does not focus buttons on click by design, so instead
    // of ignoring the done button in the editor's blur handler, we
    // need to proactively give focus away when the button is clicked
    // before the blur handler is triggered. 'click' happens after
    // 'blur', so we'll trigger on 'mousedown' and 'keydown'
    const handleDone = (e: MouseEvent | KeyboardEvent) => {
      // Only continue if left-mouse was clicked, or Enter/Space was pressed
      if (
        (e.type === 'mousedown' && (e as MouseEvent).button === 0) ||
        (e.type === 'keydown' &&
          ['Enter', ' '].includes((e as KeyboardEvent).key))
      ) {
        e.preventDefault()

        if (hasError) {
          window.location.reload()
          return
        }
        const element = document.activeElement as HTMLElement
        element.blur()
        // Ensure editor is blurred for Safari
        editor.commands.blur()
        return false
      }
    }

    const onViewModeChange = (value: string) => {
      editor.setEditable(value === 'editing')
      // Necessary to trigger an update for editor state hooks
      editor.commands.blur()
    }

    return (
      <StrictMode>
        {!disableViewModeSelector && (
          <div
            css={styles.viewModeSelectWrapper({ isNarrow: currentWidth < 450 })}
          >
            <DeprecatedSingleSelect
              initialSelection={viewModeOptions[1]}
              options={viewModeOptions}
              onChange={(option) => option && onViewModeChange(option.value)}
              css={styles.viewModeSelectOverrides({ theme })}
            />
          </div>
        )}

        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          ref={controlBarRefs}
          css={controlBarStyle}
          style={{ visibility: viewOnly ? 'hidden' : 'visible' }}
          className={classNames('text-editor-control-bar', className, {
            'is-sticky': inView,
          })}
          // Required for Safari to register event.relatedTarget on blur
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.metaKey && e.key === 'e') editor.commands.focus()
          }}
        >
          <div ref={outsideRef} css={styles.buttonGroups}>
            <ControlButtonGroup styleOverrides={controlButtonStyleOverrides}>
              <ControlButtonPopover
                label="Format Options"
                icon={
                  <Icon glyph={getHeadingIcon(activeHeadingLevel)} ariaHidden />
                }
                align="start"
              >
                <HeadingButtons isInsidePopover />
              </ControlButtonPopover>
            </ControlButtonGroup>
            {currentWidth < 895 ? (
              <Fragment>
                <ControlButtonGroup
                  styleOverrides={controlButtonStyleOverrides}
                >
                  <ControlButtonPopover
                    label="More Options"
                    icon={<Icon glyph="gem-more-horizontal" ariaHidden />}
                    displayMenuIndicator={false}
                    fixedPopoverWidth={true}
                    focusActiveOption={false}
                  >
                    <HeadingButtons isInsidePopover />
                    <FormattingButtons isInsidePopover />
                    <TableButtons
                      isInsidePopover
                      isTableActive={isActive.table}
                    />
                    <AlignButtons
                      isInsidePopover
                      activeTextAlign={activeTextAlign}
                    />
                    <ListButtons isInsidePopover />
                    <AttachmentButtons
                      isInsidePopover
                      onPickAssets={onPickAssets}
                      hyperlinkManager={hyperlinkManager}
                    />
                    <ExportButtons
                      isInsidePopover
                      documentName={documentName}
                    />
                  </ControlButtonPopover>
                </ControlButtonGroup>
                <ControlButtonGroup
                  styleOverrides={controlButtonStyleOverrides}
                >
                  <ControlButton
                    onClick={() => setShowShortcuts(true)}
                    label="Editor Shortcuts"
                    icon={<Icon glyph="gem-question-mark" ariaHidden />}
                  />
                </ControlButtonGroup>
              </Fragment>
            ) : (
              <Fragment>
                <FormattingButtons />

                <ControlButtonPopover
                  label="Align Options"
                  icon={
                    <Icon glyph={getAlignIcon(activeTextAlign)} ariaHidden />
                  }
                >
                  <AlignButtons
                    isInsidePopover
                    activeTextAlign={activeTextAlign}
                  />
                </ControlButtonPopover>

                <ControlButtonGroup
                  styleOverrides={controlButtonStyleOverrides}
                >
                  <ControlButtonPopover
                    label="List Options"
                    icon={
                      <Icon glyph={getListIcon(activeListType)} ariaHidden />
                    }
                  >
                    <ListButtons isInsidePopover />
                  </ControlButtonPopover>
                </ControlButtonGroup>

                <AttachmentButtons
                  onPickAssets={onPickAssets}
                  hyperlinkManager={hyperlinkManager}
                />
                <TableButtons isTableActive={isActive.table} />
                {!disableFullScreenAndCenterControls && (
                  <ControlButtonGroup
                    styleOverrides={controlButtonStyleOverrides}
                  >
                    <ControlButton
                      onClick={() =>
                        setFullScreen && setFullScreen(!isFullScreen)
                      }
                      isActive={isFullScreen}
                      label="Toggle Fullscreen Mode"
                      icon={<Icon glyph="gem-full-screen" ariaHidden />}
                    />
                    {!isFullScreen && (
                      <ControlButton
                        onClick={(chain) => chain.toggleCenterJustified()}
                        isActive={['centerJustify']}
                        label={({ isActive }) =>
                          isActive ? 'Left-Align Document' : 'Center Document'
                        }
                        icon={<Icon glyph="gem-indent" ariaHidden />}
                      />
                    )}
                  </ControlButtonGroup>
                )}
                <ControlButtonGroup
                  styleOverrides={controlButtonStyleOverrides}
                >
                  <ControlButton
                    onClick={() => setShowShortcuts(true)}
                    label="Editor Shortcuts"
                    icon={<Icon glyph="gem-question-mark" ariaHidden />}
                  />
                </ControlButtonGroup>
                <ExportButtons documentName={documentName} />
              </Fragment>
            )}
          </div>
          <div
            css={styles.saveArea({ theme, isFullScreen, hasError })}
            className="LastSavedTime"
          >
            {saveState != null &&
              currentWidth > 540 &&
              !disableLastSavedTime &&
              ['saved', 'saving'].includes(saveState.state) && (
                <span>
                  {saveState.state === 'saved' ? lastSaveTime : 'Saving...'}
                </span>
              )}
            {hasError && (
              <Tooltip
                content="A conflict occurred when saving your changes. Click 'View Updates' to reload and see the latest changes."
                zIndex={10}
              >
                <span>
                  <Icon glyph="gem-info-circle" ariaHidden />
                </span>
              </Tooltip>
            )}
            {!isFullScreen && !disableDoneButton && (
              <Button
                variant="primary"
                buttonText={hasError ? 'View Updates' : 'Done'}
                name={hasError ? 'view updates' : 'done'}
                onKeyDownCapture={handleDone}
                onMouseDownCapture={handleDone}
              />
            )}
          </div>
        </div>
      </StrictMode>
    )
  }
)
FullControlBar.displayName = 'FullControlBar'
